import { FunctionComponent } from 'react';
import { useOptimizelyExperiment } from '../../../../hooks/useOptimizelyExperiment';
import { getGovernmentDataPartnerBannerVariables } from '../helpers/getGovernmentDataPartnerBannerVariables';
import { GovernmentDataPartnerBanner } from './GovernmentDataPartnerBanner';

export type PartnerBannerVariant = 'FOR_RENT' | 'FOR_SALE' | 'OFF_MARKET';

interface ExperimentConfig {
  experimentId: string;
  customEventId?: string;
  variation?: Record<string, string>;
}

const governmentDataPartnerForSaleNTExperimentConfig: ExperimentConfig = {
  experimentId: 'government_data_partner_banner_for_sale_nt',
  customEventId: 'government-data-partner-nt-click-track-metric',
};

const governmentDataPartnerForSaleExperimentConfig: ExperimentConfig = {
  experimentId: 'government_data_partner_banner_for_sale',
  customEventId: 'government-data-partner-click-track-metric',
};

const governmentDataPartnerForRentNTExperimentConfig: ExperimentConfig = {
  experimentId: 'government_data_partner_banner_for_rent_nt',
  customEventId: 'government-data-partner-nt-click-track-metric',
};

const governmentDataPartnerForRentExperimentConfig: ExperimentConfig = {
  experimentId: 'government_data_partner_banner_for_rent',
  customEventId: 'government-data-partner-click-track-metric',
};

const governmentDataPartnerOffMarketNTExperimentConfig: ExperimentConfig = {
  experimentId: 'government_data_partner_banner_off_market_nt',
  customEventId: 'government-data-partner-nt-click-track-metric',
};

const governmentDataPartnerOffMarketExperimentConfig: ExperimentConfig = {
  experimentId: 'government_data_partner_banner_off_market',
  customEventId: 'government-data-partner-click-track-metric',
};

const getGovernmentDataPartnerExperimentConfig = (
  partnerBannerVariant: PartnerBannerVariant,
  isPropertyNorthernTerritory: boolean,
): ExperimentConfig => {
  switch (partnerBannerVariant) {
    case 'FOR_SALE':
      return isPropertyNorthernTerritory
        ? governmentDataPartnerForSaleNTExperimentConfig
        : governmentDataPartnerForSaleExperimentConfig;
    case 'FOR_RENT':
      return isPropertyNorthernTerritory
        ? governmentDataPartnerForRentNTExperimentConfig
        : governmentDataPartnerForRentExperimentConfig;
    case 'OFF_MARKET':
      return isPropertyNorthernTerritory
        ? governmentDataPartnerOffMarketNTExperimentConfig
        : governmentDataPartnerOffMarketExperimentConfig;
  }
};

interface GovernmentDataPartnerBannerWithVariantProps {
  partnerBannerVariant: PartnerBannerVariant;
  isPropertyNorthernTerritory: boolean;
  state: string | null;
}

export const GovernmentDataPartnerBannerWithVariant: FunctionComponent<
  GovernmentDataPartnerBannerWithVariantProps
> = ({ partnerBannerVariant, isPropertyNorthernTerritory, state }) => {
  const { experimentId, customEventId }: ExperimentConfig =
    getGovernmentDataPartnerExperimentConfig(partnerBannerVariant, isPropertyNorthernTerritory);

  const {
    variables: experimentVariables,
    variation: experimentVariation,
    trackCustomEvent,
  } = useOptimizelyExperiment(experimentId, customEventId);

  if (!experimentVariables || !experimentVariation) return null;

  const { title, description, disclaimerText, ctaText, ctaLink } =
    getGovernmentDataPartnerBannerVariables(experimentVariables);

  return (
    <GovernmentDataPartnerBanner
      title={title}
      description={description}
      disclaimerText={disclaimerText}
      ctaText={ctaText}
      ctaLink={ctaLink}
      experimentId={experimentId}
      experimentVariation={experimentVariation}
      isPropertyNorthernTerritory={isPropertyNorthernTerritory}
      onCtaClick={trackCustomEvent}
      state={state}
      partnerBannerVariant={partnerBannerVariant}
    />
  );
};
